/** Path builders for the /api routes. Every id is URI-encoded. */

const enc = encodeURIComponent;

const ws = (workspaceId: string) => `/api/workspaces/${enc(workspaceId)}`;
const cfg = (workspaceId: string, configurationId: string) =>
  `${ws(workspaceId)}/configurations/${enc(configurationId)}`;

export const paths = {
  me: "/api/auth/me",
  workspaces: "/api/workspaces",
  logout: "/api/auth/logout",
  devLogin: "/api/auth/dev-login",
  googleStart: "/api/auth/google/start",
  desktopDownloadInfo: "/api/downloads/desktop/info",
  desktopDownload: "/api/downloads/desktop",

  workspace: (workspaceId: string) => ws(workspaceId),
  usage: (workspaceId: string) => `${ws(workspaceId)}/usage`,

  persons: (workspaceId: string, deleted = false) =>
    `${ws(workspaceId)}/persons${deleted ? "?status=deleted" : ""}`,
  personInvite: (workspaceId: string) => `${ws(workspaceId)}/persons/invite`,
  person: (workspaceId: string, personId: string) => `${ws(workspaceId)}/persons/${enc(personId)}`,
  personRestore: (workspaceId: string, personId: string) =>
    `${ws(workspaceId)}/persons/${enc(personId)}/restore`,
  personLink: (workspaceId: string, personId: string) =>
    `${ws(workspaceId)}/persons/${enc(personId)}/link`,

  media: (workspaceId: string) => `${ws(workspaceId)}/media`,
  mediaFile: (workspaceId: string, mediaId: string) => `${ws(workspaceId)}/media/${enc(mediaId)}`,

  configurations: (workspaceId: string, deleted = false) =>
    `${ws(workspaceId)}/configurations${deleted ? "?status=deleted" : ""}`,
  configuration: (workspaceId: string, configurationId: string) =>
    cfg(workspaceId, configurationId),
  configurationRestore: (workspaceId: string, configurationId: string) =>
    `${cfg(workspaceId, configurationId)}/restore`,
  configurationDuplicate: (workspaceId: string, configurationId: string) =>
    `${cfg(workspaceId, configurationId)}/duplicate`,
  configurationChanges: (workspaceId: string, configurationId: string) =>
    `${cfg(workspaceId, configurationId)}/changes`,
  markDownloaded: (workspaceId: string, configurationId: string) =>
    `${cfg(workspaceId, configurationId)}/mark-downloaded`,
  exportInput: (workspaceId: string, configurationId: string) =>
    `${cfg(workspaceId, configurationId)}/export-input`,
  slots: (workspaceId: string, configurationId: string) =>
    `${cfg(workspaceId, configurationId)}/slots`,
  apiKeys: (workspaceId: string, configurationId: string) =>
    `${cfg(workspaceId, configurationId)}/api-keys`,
  apiKey: (workspaceId: string, configurationId: string, keyId: string) =>
    `${cfg(workspaceId, configurationId)}/api-keys/${enc(keyId)}`,

  deviceCodes: (workspaceId: string) => `${ws(workspaceId)}/device-codes`,
  devices: (workspaceId: string) => `${ws(workspaceId)}/devices`,
  device: (workspaceId: string, deviceId: string) => `${ws(workspaceId)}/devices/${enc(deviceId)}`,

  members: (workspaceId: string) => `${ws(workspaceId)}/members`,
  member: (workspaceId: string, operatorId: string) =>
    `${ws(workspaceId)}/members/${enc(operatorId)}`,
  invites: (workspaceId: string) => `${ws(workspaceId)}/invites`,
  invite: (workspaceId: string, inviteId: string) => `${ws(workspaceId)}/invites/${enc(inviteId)}`,

  invitePeek: (token: string) => `/api/invites/${enc(token)}/peek`,
  actAs: "/api/admin/act-as",
};

/** A media upload query string, as the API's mediaUploadQuerySchema expects it. */
export function mediaUploadQuery(params: {
  kind: "photo" | "song";
  ext: string;
  sha256: string;
  durationMs?: number;
}): string {
  const search = new URLSearchParams({
    kind: params.kind,
    ext: params.ext,
    sha256: params.sha256,
  });
  if (params.durationMs !== undefined) {
    search.set("duration_ms", String(params.durationMs));
  }
  return `?${search.toString()}`;
}
